import { RadiosetWebElementBase } from './RadiosetWebElementBase';
import { error, WebElement } from 'selenium-webdriver';

/**
 * The component WebElement for [oj-c-radioset](../../jsdocs/oj-c.Radioset.html).
 * Do not instantiate this class directly, instead, use
 * [findRadioset](../functions/findRadioset.html).
 */
export class RadiosetWebElement extends RadiosetWebElementBase {
  /**
   * Sets the value of "value" property for the Radioset component
   * @param value The value to set for "value"
   * @throws {ElementNotInteractableError} if the API is called when the control is readonly or disabled
   * @throws {InvalidArgumentError} if the value is non-existent
   */
  async changeValue(value: any) {
    // Check if the control is interactable
    if (!(await this.isInteractable())) {
      // Cannot interact when the control is disabled or readonly
      throw new error.ElementNotInteractableError(
        'oj-c-radioset: Value cannot be changed when the control is disabled or readonly'
      );
    }
    await this.whenBusyContextReady();
    // Get the oj-c-radioset element using the value property
    const label = await this.getRadioItemLabel(value);
    // If the label for the input was not found, then the provided value does not exist in the
    // oj-c-radioset element
    if (label == null) {
      throw new error.InvalidArgumentError(`oj-c-radioset: ${value} is not a valid value.`);
    }
    await label?.click();
  }

  /**
   * Checks if the Radioset is in interactable state
   */
  private async isInteractable(): Promise<boolean> {
    const isReadonly = await this.getReadonly();
    const isDisabled = await this.getDisabled();
    return !(isReadonly || isDisabled);
  }

  /**
   * Finds the label of the input element for the provided value if it exists
   *
   * @param value The value for which the radio element has to be fetched
   * @returns The corresponding label of the input element if it exists, null otherwise
   */
  private async getRadioItemLabel(value: any): Promise<WebElement | null> {
    return await this.getDriver().executeScript<WebElement | null>(
      (element: HTMLElement, searchValue: string) => {
        const result = element.querySelector(`input[type="radio"][value="${searchValue}"]`);
        return result?.closest('label');
      },
      this,
      value
    );
  }
}
